import React from "react";
import PropTypes from "prop-types";
import Button from "@dtci/spellbook/Button";
import { STAT_TILE_FILTER } from "ppt/constants";
import styles from "./pptStatTiles.module.scss";

/**
 * Stat tiles that quick-filter a grid to one severity bucket:
 *
 *   OK        → rows with no errors or warnings
 *   Warnings  → WARNING-severity rows
 *   Errors    → ERROR-severity rows
 *   Disabled  → inactive rows (opt in via visibleTiles)
 *
 * Click semantics:
 *   - Click an unselected tile → that filter becomes active (any previously
 *     selected tile deselects, since only one filter can be active at a time)
 *   - Click the currently-selected tile → clears the filter (no tile selected,
 *     grid shows everything)
 *
 * Presentational and domain-agnostic: callers supply counts keyed by
 * STAT_TILE_FILTER value and own the filter state.
 * Zero-count tiles stay in the layout (muted) so the row keeps a stable rhythm.
 */

const TILES = [
  { key: STAT_TILE_FILTER.OK, label: "OK", tone: "ok" },
  { key: STAT_TILE_FILTER.WARNING, label: "Warnings", tone: "warning" },
  { key: STAT_TILE_FILTER.ERROR, label: "Errors", tone: "error" },
  { key: STAT_TILE_FILTER.DISABLED, label: "Disabled", tone: "muted" },
];

const DEFAULT_VISIBLE_TILES = TILES.map((tile) => tile.key);

const filterLabelFor = (tileKey, tileLabel) => {
  if (tileKey === STAT_TILE_FILTER.DISABLED) {
    return "Filter to disabled items";
  }
  if (tileKey === STAT_TILE_FILTER.OK) {
    return "Filter to items with no errors or warnings";
  }
  return `Filter to items with ${tileLabel.toLowerCase()}`;
};

export const PPTStatTiles = ({
  counts,
  selectedFilter,
  onSelect,
  visibleTiles = DEFAULT_VISIBLE_TILES,
}) => (
  <div className={styles.tileGridCompact}>
    {TILES.filter((tile) => visibleTiles.includes(tile.key)).map((tile) => {
      const count = counts?.[tile.key] ?? 0;
      const isSelected = selectedFilter === tile.key;
      const tileClassName = [
        styles[`tile_${tile.tone}`],
        isSelected ? styles.tileSelected : "",
        count === 0 ? styles.tileZero : "",
      ]
        .filter(Boolean)
        .join(" ");

      // Toggle: clicking an already-selected tile clears the filter.
      const onClick = () => onSelect(isSelected ? null : tile.key);
      const filterLabel = filterLabelFor(tile.key, tile.label);

      return (
        <Button
          key={tile.key}
          type="button"
          styleType="standard"
          colorType="base"
          className={tileClassName}
          onClick={onClick}
          aria-pressed={isSelected}
          aria-label={filterLabel}
          title={filterLabel}
        >
          <span className={styles.tileCountCompact}>{count}</span>
          <span className={styles.tileLabelCompact}>{tile.label}</span>
        </Button>
      );
    })}
  </div>
);

PPTStatTiles.propTypes = {
  /** Counts keyed by STAT_TILE_FILTER value */
  counts: PropTypes.object,
  selectedFilter: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  visibleTiles: PropTypes.arrayOf(PropTypes.string),
};
